// src/components/common/CustomButton.tsx
import * as React from "react";
import { Button } from "antd";
import type { ButtonProps } from "antd";
import styled, { css } from "styled-components";

const PRIMARY_COLOR = "#546E7A";
const PRIMARY_HOVER = "#455A64";
const DANGER_COLOR = "#d32f2f";
const DANGER_HOVER = "#b71c1c";

type Variant = "contained" | "outlined" | "text";

const buttonStyles = ({
  $variant,
  $customVariant,
  $size,
}: {
  $variant: Variant;
  $customVariant: "primary" | "danger";
  $size?: ButtonProps["size"];
}) => {
  const mainColor = $customVariant === "danger" ? DANGER_COLOR : PRIMARY_COLOR;
  const hoverColor = $customVariant === "danger" ? DANGER_HOVER : PRIMARY_HOVER;
  const hoverBg =
    $customVariant === "danger" ? "rgba(211, 47, 47, 0.08)" : "rgba(84, 110, 122, 0.08)";

  return css`
    && {
      text-transform: none;
      font-weight: 600;
      font-size: 13px;
      white-space: nowrap;
      ${$size === "small" &&
      css`
        height: 32px;
        font-size: 12px;
        padding: 0 12px;
      `}
    }

    &&.ant-btn {
      ${$variant === "contained" &&
      css`
        background-color: ${mainColor};
        color: #fff;
        border-color: ${mainColor};
        &:hover {
          background-color: ${hoverColor};
          border-color: ${hoverColor};
          color: #fff;
        }
        &:disabled {
          background-color: #b0bec5;
          border-color: #b0bec5;
          color: #fff;
        }
      `}

      ${$variant === "outlined" &&
      css`
        background-color: transparent;
        color: ${mainColor};
        border-color: ${mainColor};
        &:hover {
          background-color: ${hoverBg};
          border-color: ${hoverColor};
          color: ${mainColor};
        }
        &:disabled {
          color: #b0bec5;
          border-color: #b0bec5;
        }
      `}

      ${$variant === "text" &&
      css`
        background-color: transparent;
        color: ${mainColor};
        border-color: transparent;
        box-shadow: none;
        &:hover {
          background-color: ${hoverBg};
          color: ${mainColor};
        }
        &:disabled {
          color: #b0bec5;
        }
      `}
    }
  `;
};

const StyledButton = styled(Button)<{
  $variant: Variant;
  $customVariant: "primary" | "danger";
  $size?: ButtonProps["size"];
}>`
  ${buttonStyles}
`;

export type CustomButtonProps = Omit<ButtonProps, "color"> & {
  isLoading?: boolean;
  customVariant?: "primary" | "danger";
  variant?: Variant;
  component?: React.ElementType;
  to?: string;
  [key: string]: any;
};

const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    { variant = "contained", isLoading, children, disabled, customVariant = "primary", ...props },
    ref
  ) => {
    return (
      <StyledButton
        ref={ref}
        type={variant === "contained" ? "primary" : variant === "text" ? "text" : "default"}
        disabled={isLoading || disabled}
        loading={isLoading}
        $variant={variant}
        $customVariant={customVariant}
        $size={props.size}
        {...props}
      >
        {children}
      </StyledButton>
    );
  }
);

CustomButton.displayName = "CustomButton";

export default CustomButton;
